import { FC } from "react";

import { Box } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";

import { FormMessage, FullWidthVStack } from "@components/common";
import Icon from "@components/Icon";
import Loader from "@components/Loader";
import Message from "@components/Message";
import { OptionButtonGroup } from "@components/OptionButton";
import { Desktop, Mobile } from "@lib/media";

import { DeliveryMethod as DeliveryMethodValues, OrderFields } from "../../constants";
import useOrderData from "../../context/useOrderData";
import useOrderFieldsLoading from "../../context/useOrderFieldsLoading";
import useOrderFormik from "../../hooks/useOrderFormik";
import ArrivalMessage from "../common/ArrivalMessage";
import SectionTitle from "../common/SectionTitle";

type TDeliveryMethodProps = {
  name: string;
  onChange: (deliveryMethod: DeliveryMethodValues) => void;
};

const DeliveryMethod: FC<TDeliveryMethodProps> = ({ name, onChange }) => {
  const { t } = useTranslation();

  const { values, errors } = useOrderFormik();

  const selectedDeliveryMethod = values[OrderFields.DELIVERY_METHOD];
  const fieldError = errors[name];

  const {
    staticData: { deliveryMethods },
    dynamicData: { offers },
  } = useOrderData();

  const selectedOffer = offers.find(
    ({ deliveryMethod: offerDeliveryMethod }) => offerDeliveryMethod === selectedDeliveryMethod
  );

  const { isFieldLoading } = useOrderFieldsLoading();
  const isLoading = isFieldLoading(name);

  const deliveryMethodsInOffers = offers.map(({ deliveryMethod }) => deliveryMethod);

  const deliveryMethodOptions = deliveryMethods.map(({ label, value }) => ({
    label,
    value,
    isDisabled: !deliveryMethodsInOffers.includes(value),
    width: "auto",
  }));

  return (
    <Loader isLoading={isLoading}>
      <FullWidthVStack>
        <SectionTitle>{t("order-package-delivery_method")}</SectionTitle>
        {selectedOffer?.notification && (
          <Box sx={{ alignSelf: "start" }}>
            <Message align="start" icon={<Icon icon="info" size="1.25rem" />} sx={{ py: "0.7rem" }}>
              <Box fontSize="1rem" lineHeight="1.3">
                {selectedOffer.notification}
              </Box>
            </Message>
          </Box>
        )}
        <Box>
          <Mobile>
            <OptionButtonGroup
              name={name}
              options={deliveryMethodOptions}
              lines={[{ length: 1 }]}
              onChange={(value) => onChange(value as DeliveryMethodValues)}
            />
          </Mobile>
          <Desktop>
            <OptionButtonGroup
              name={name}
              options={deliveryMethodOptions}
              lines={[{ length: 4 }]}
              onChange={(value) => onChange(value as DeliveryMethodValues)}
            />
          </Desktop>
        </Box>
        <ArrivalMessage />
        {fieldError && <FormMessage message={fieldError} isInvalid />}
      </FullWidthVStack>
    </Loader>
  );
};
export default DeliveryMethod;
